/**
 * Route manifest: the only enumeration of indexable URLs.
 * Consumed by the sitemap generator, the prerenderer and the SEO gate through
 * `window.__SEO_ROUTES__` (see `src/index.js`).
 *
 * Intentionally free of React imports so the shape stays plain data.
 */

import { CATEGORIES, GUIDES, BEST_OF } from "../data/content";
import { toIsoDate } from "./seo";

/**
 * @typedef {Object} SeoRoute
 * @property {string} path      Relative URL with a leading slash, no trailing slash (root = "/").
 * @property {number} priority  0.1–1.0, written to the sitemap.
 * @property {string|null} lastmod ISO date, or null when no real date exists.
 */

const PRIORITY = {
  home: 1.0,
  sectionIndex: 0.8,
  detail: 0.7,
  /** About/Contact: informational, below section indexes but above legal boilerplate. */
  informational: 0.5,
  legal: 0.3,
};

const STATIC_ROUTES = [
  { path: "/", priority: PRIORITY.home },
  { path: "/guide", priority: PRIORITY.sectionIndex },
  { path: "/categorie", priority: PRIORITY.sectionIndex },
  { path: "/chi-sono", priority: PRIORITY.informational },
  { path: "/contatti", priority: PRIORITY.informational },
  { path: "/disclosure", priority: PRIORITY.legal },
  { path: "/privacy", priority: PRIORITY.legal },
  { path: "/cookie", priority: PRIORITY.legal },
  { path: "/termini", priority: PRIORITY.legal },
  { path: "/note-legali", priority: PRIORITY.legal },
];

/** Nestly has no publication dates yet, so this resolves to null for every article. */
function articleLastmod(article) {
  return toIsoDate(article.updatedAt) || toIsoDate(article.publishedAt);
}

/**
 * Builds the deterministic list of indexable routes: static pages first, then
 * dynamic slugs in content order. Excludes the catch-all and query-string URLs.
 * @returns {SeoRoute[]}
 */
export function getSeoRoutes() {
  const routes = [
    ...STATIC_ROUTES.map((route) => ({ ...route, lastmod: null })),
    ...CATEGORIES.map((category) => ({
      path: `/categoria/${category.slug}`,
      priority: PRIORITY.detail,
      lastmod: null,
    })),
    ...GUIDES.map((guide) => ({
      path: `/guida/${guide.slug}`,
      priority: PRIORITY.detail,
      lastmod: articleLastmod(guide),
    })),
    ...BEST_OF.map((list) => ({
      path: `/best-of/${list.slug}`,
      priority: PRIORITY.detail,
      lastmod: articleLastmod(list),
    })),
  ];

  const seen = new Set();
  return routes.filter((route) => {
    if (seen.has(route.path)) return false;
    seen.add(route.path);
    return true;
  });
}
